import { Filter } from '@fabric-ui/components';
import { AST, Binary, ColumnRef, ExpressionValue, ExprList, Function, Parser as NodeSqlParser } from 'node-sql-parser';

const parser = new NodeSqlParser();

export const FREE_TEXT_COLUMN = '__FREE_TEXT__';

const createColumnRef = (column: string): ColumnRef => ({ type: 'column_ref', table: null, column }) as const;
const createStringLiteral = (v: string) =>
  v.includes('"') ? { type: 'single_quote_string', value: v } : ({ type: 'double_quote_string', value: v } as const);
const createNumberLiteral = (v: number) => ({ type: 'number', value: v }) as const;
const createBoolLiteral = (v: boolean) => ({ type: 'bool', value: v }) as const;
const createNullLiteral = () => ({ type: 'null', value: null }) as const;
const createExpressionList = <T>(values: T[]) => ({ type: 'expr_list', value: values }) as const;

const createStringInList = (values: (string | null)[]) =>
  createExpressionList(values.map(v => (v === null ? createNullLiteral() : createStringLiteral(v))));

type Literal =
  | ReturnType<typeof createStringLiteral>
  | ReturnType<typeof createNumberLiteral>
  | ReturnType<typeof createBoolLiteral>
  | ReturnType<typeof createNullLiteral>
  | ReturnType<typeof createExpressionList>;

const createFunction = (name: string, args: Literal[] = []): Function =>
  ({ type: 'function', args: createExpressionList(args), name: { name: [{ type: 'default', value: name }] } }) as const;

const createBinaryExpr = (left: ColumnRef, operator: string, right: ExprList | ExpressionValue): Binary =>
  ({ type: 'binary_expr', operator, left, right }) as const;

const convertStringCondition = (columnRef: ColumnRef, expr: NonNullable<Filter['expression']>): Binary => {
  const c = expr.stringCondition;

  if (c && 'equals' in c && c.equals !== undefined) {
    return createBinaryExpr(columnRef, '=', createStringLiteral(c.equals));
  }
  if (c && 'notEqual' in c && c.notEqual !== undefined) {
    return createBinaryExpr(columnRef, '!=', createStringLiteral(c.notEqual));
  }
  if (c && 'contains' in c) {
    return createBinaryExpr(columnRef, 'LIKE', createStringLiteral(`%${c.contains}%`));
  }
  if (c && 'starts' in c) {
    return createBinaryExpr(columnRef, 'LIKE', createStringLiteral(`${c.starts}%`));
  }
  if (c && 'ends' in c) {
    return createBinaryExpr(columnRef, 'LIKE', createStringLiteral(`%${c.ends}`));
  }
  if (c && 'notContains' in c) {
    return createBinaryExpr(columnRef, 'NOT LIKE', createStringLiteral(`%${c.notContains}%`));
  }
  if (c && 'in' in c && c.in) {
    return createBinaryExpr(columnRef, 'IN', createStringInList(c.in.values));
  }
  if (c && 'notIn' in c && c.notIn) {
    return createBinaryExpr(columnRef, 'NOT IN', createStringInList(c.notIn.values));
  }
  if (c && 'empty' in c) {
    return createBinaryExpr(columnRef, 'IS', createNullLiteral());
  }
  if (c && 'notEmpty' in c) {
    return createBinaryExpr(columnRef, 'IS NOT', createNullLiteral());
  }

  throw new Error(`Unsupported string condition for field ${expr.fieldName}`);
};

const convertNumberCondition = (columnRef: ColumnRef, expr: NonNullable<Filter['expression']>): Binary => {
  const c = expr.numberCondition;
  if (c && 'equals' in c && c.equals !== undefined) {
    return createBinaryExpr(columnRef, '=', createNumberLiteral(c.equals));
  }
  if (c && 'notEqual' in c && c.notEqual !== undefined) {
    return createBinaryExpr(columnRef, '!=', createNumberLiteral(c.notEqual));
  }
  if (c && 'gt' in c && c.gt !== undefined) {
    return createBinaryExpr(columnRef, '>', createNumberLiteral(c.gt));
  }
  if (c && 'lt' in c && c.lt !== undefined) {
    return createBinaryExpr(columnRef, '<', createNumberLiteral(c.lt));
  }
  if (c && 'gte' in c && c.gte !== undefined) {
    return createBinaryExpr(columnRef, '>=', createNumberLiteral(c.gte));
  }
  if (c && 'lte' in c && c.lte !== undefined) {
    return createBinaryExpr(columnRef, '<=', createNumberLiteral(c.lte));
  }
  if (c && 'between' in c && c.between) {
    const expressionList = [createNumberLiteral(c.between.from), createNumberLiteral(c.between.to)];
    return createBinaryExpr(columnRef, 'BETWEEN', createExpressionList(expressionList));
  }
  if (c && 'empty' in c) {
    return createBinaryExpr(columnRef, 'IS', createNullLiteral());
  }
  if (c && 'notEmpty' in c) {
    return createBinaryExpr(columnRef, 'IS NOT', createNullLiteral());
  }

  throw new Error(`Unsupported number condition for field ${expr.fieldName}`);
};

const convertBoolCondition = (columnRef: ColumnRef, expr: NonNullable<Filter['expression']>): Binary => {
  const c = expr.boolCondition;
  if (c && 'equals' in c && c.equals !== undefined) {
    return createBinaryExpr(columnRef, '=', createBoolLiteral(c.equals));
  }
  if (c && 'notEqual' in c && c.notEqual !== undefined) {
    return createBinaryExpr(columnRef, '!=', createBoolLiteral(!c.equals));
  }
  if (c && 'empty' in c) {
    return createBinaryExpr(columnRef, 'IS', createNullLiteral());
  }
  if (c && 'notEmpty' in c) {
    return createBinaryExpr(columnRef, 'IS NOT', createNullLiteral());
  }

  throw new Error(`Unsupported boolean condition for field ${expr.fieldName}`);
};

const convertDateCondition = (columnRef: ColumnRef, expr: NonNullable<Filter['expression']>): Binary => {
  const c = expr.dateCondition;
  if (c && 'between' in c && c.between) {
    const [from, to] = c.between.values;
    const expressionList = [createStringLiteral(from.toISOString()), createStringLiteral(to.toISOString())];
    return createBinaryExpr(columnRef, 'BETWEEN', createExpressionList(expressionList));
  }
  if (c && 'empty' in c) {
    return createBinaryExpr(columnRef, 'IS', createNullLiteral());
  }
  if (c && 'notEmpty' in c) {
    return createBinaryExpr(columnRef, 'IS NOT', createNullLiteral());
  }
  if (c && 'relative' in c && c.relative) {
    const { periodBehaviour, value, unit } = c.relative;
    const fn = createFunction('RELATIVE_DATE', [
      createStringLiteral(String(periodBehaviour)),
      createNumberLiteral(Number(value)),
      createStringLiteral(String(unit)),
    ]);
    return createBinaryExpr(columnRef, '=', fn);
  }

  throw new Error(`Unsupported date condition for field ${expr.fieldName}`);
};

const convertExpressionToAst = (expr: NonNullable<Filter['expression']>): Binary => {
  if (!expr.fieldName) {
    throw new Error('Expression must have a fieldName');
  }

  const columnRef = createColumnRef(expr.fieldName);

  if (expr.stringCondition) {
    return convertStringCondition(columnRef, expr);
  }
  if (expr.numberCondition) {
    return convertNumberCondition(columnRef, expr);
  }
  if (expr.boolCondition) {
    return convertBoolCondition(columnRef, expr);
  }
  if (expr.dateCondition) {
    return convertDateCondition(columnRef, expr);
  }

  throw new Error(`Unsupported expression type for field ${expr.fieldName}`);
};

export type FilterToSqlOptions = { preserveGroups?: boolean };

const convertFreeTextToAst = (freeText: NonNullable<Filter['freeText']>): any => {
  if (freeText.stringValue !== undefined) {
    const columnRef = createColumnRef(FREE_TEXT_COLUMN);
    return createBinaryExpr(columnRef, 'LIKE', createStringLiteral(freeText.stringValue));
  }
  throw new Error('FreeText must have a stringValue');
};

export const createConverter = (options: FilterToSqlOptions = {}) => {
  const { preserveGroups = false } = options;

  const needsParentheses = (parentOp: 'AND' | 'OR' | null, op: 'AND' | 'OR'): boolean => {
    const isOrInsideAnd = () => parentOp === 'AND' && op === 'OR';
    const isAndInsideOr = () => parentOp === 'OR' && op === 'AND';
    const shouldPreserveGroups = () => !preserveGroups;
    const isRootNode = () => parentOp === null;
    const isSameOperatorNested = () => parentOp === op;

    /* Order matters */
    if (isOrInsideAnd()) return true;
    if (isAndInsideOr()) return true;
    if (shouldPreserveGroups()) return false;
    if (isRootNode()) return false;
    if (isSameOperatorNested()) return true;

    return false;
  };

  const convertFilterToAst = (filter: Filter, parentOp: 'AND' | 'OR' | null = null): Binary => {
    if (filter.expression) {
      return convertExpressionToAst(filter.expression);
    }

    if (filter.freeText) {
      return convertFreeTextToAst(filter.freeText);
    }

    const buildGroup = (operands: Filter[], op: 'AND' | 'OR'): Binary => {
      const [first, ...rest] = operands;
      const node = rest.reduce<Binary>(
        (left, f) => ({
          type: 'binary_expr',
          operator: op,
          left,
          right: convertFilterToAst(f, op),
        }),
        convertFilterToAst(first, op)
      );

      if (needsParentheses(parentOp, op)) {
        node.parentheses = true;
      }
      return node;
    };

    if (filter.and) {
      return buildGroup(filter.and.operands, 'AND');
    }
    if (filter.or) {
      return buildGroup(filter.or.operands, 'OR');
    }

    throw new Error('Filter must have either an expression, freeText, or logical operators (and/or)');
  };

  return convertFilterToAst;
};

export const filterToSqlConverter = (filter: Filter, table = 'log', options?: FilterToSqlOptions): string => {
  const convertFilterToAst = createConverter(options);
  const where = convertFilterToAst(filter);
  const ast: AST = {
    type: 'select',
    columns: [{ expr: { type: 'star' }, as: null }],
    from: [{ db: null, table, as: null, parentheses: false }],
    where,
    distinct: null,
    orderby: [],
    limit: null,
    having: null,
    options: null,
    with: [],
    groupby: { columns: [], modifiers: [] },
  };
  return parser.sqlify(ast);
};
